import { useState } from 'react';
import { clients as clientsApi, ds3 as ds3Api } from '../api/client.js';

export default function ImportPage() {
  const [clientFile, setClientFile] = useState(null);
  const [itemFile, setItemFile] = useState(null);
  const [ds3File, setDs3File] = useState(null);
  const [clientResult, setClientResult] = useState(null);
  const [itemResult, setItemResult] = useState(null);
  const [ds3Result, setDs3Result] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleClientImport = async () => {
    if (!clientFile) return;
    setLoading(true);
    setError('');
    setClientResult(null);
    try {
      const result = await clientsApi.import(clientFile);
      setClientResult(result);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleItemImport = async () => {
    if (!itemFile) return;
    setLoading(true);
    setError('');
    setItemResult(null);
    try {
      const result = await clientsApi.importItems(itemFile);
      setItemResult(result);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-8">
      <h2 className="text-xl font-bold">Import de données</h2>

      {error && <p className="text-red-600 bg-red-50 p-3 rounded-lg">{error}</p>}

      {/* Import clients */}
      <section className="bg-white rounded-xl p-6 shadow-sm">
        <h3 className="font-semibold text-lg mb-2">Importer des clients</h3>
        <p className="text-sm text-gray-500 mb-4">
          Fichier Excel/CSV avec colonnes : nom, adresse, ville, CP, telephone, email, nb_velos, creneau
        </p>
        <div className="flex gap-3 items-center">
          <input
            type="file"
            accept=".xlsx,.xls,.csv"
            onChange={(e) => setClientFile(e.target.files[0])}
            className="text-sm"
          />
          <button
            onClick={handleClientImport}
            disabled={!clientFile || loading}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm disabled:opacity-50"
          >
            {loading ? 'Import...' : 'Importer'}
          </button>
        </div>
        {clientResult && (
          <div className="mt-4 p-3 bg-green-50 rounded-lg text-sm">
            <p className="font-medium text-green-800">
              {clientResult.imported} clients importés, {clientResult.duplicates} doublons ignorés
            </p>
            {clientResult.errors?.length > 0 && (
              <ul className="mt-2 text-red-600">
                {clientResult.errors.map((e, i) => <li key={i}>{e}</li>)}
              </ul>
            )}
            <p className="text-gray-500 mt-1">Le geocoding est en cours en arriere-plan.</p>
          </div>
        )}
      </section>

      {/* Import items */}
      <section className="bg-white rounded-xl p-6 shadow-sm">
        <h3 className="font-semibold text-lg mb-2">Importer des unités (FNUCI)</h3>
        <p className="text-sm text-gray-500 mb-4">
          Fichier Excel/CSV avec colonnes : FNUCI (ou serial), N° boite (optionnel)
        </p>
        <div className="flex gap-3 items-center">
          <input
            type="file"
            accept=".xlsx,.xls,.csv"
            onChange={(e) => setItemFile(e.target.files[0])}
            className="text-sm"
          />
          <button
            onClick={handleItemImport}
            disabled={!itemFile || loading}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm disabled:opacity-50"
          >
            {loading ? 'Import...' : 'Importer'}
          </button>
        </div>
        {itemResult && (
          <div className="mt-4 p-3 bg-green-50 rounded-lg text-sm">
            <p className="font-medium text-green-800">
              {itemResult.imported} unités importées, {itemResult.duplicates} doublons ignorés
            </p>
            {itemResult.errors?.length > 0 && (
              <ul className="mt-2 text-red-600">
                {itemResult.errors.map((e, i) => <li key={i}>{e}</li>)}
              </ul>
            )}
          </div>
        )}
      </section>
      {/* Import DS3 mappings */}
      <section className="bg-white rounded-xl p-6 shadow-sm">
        <h3 className="font-semibold text-lg mb-2">Importer le fichier DS3 (cross-validation)</h3>
        <p className="text-sm text-gray-500 mb-4">
          Fichier Excel/CSV avec colonnes : boite (ou box), FNUCI (ou serial). Permet de fiabiliser l'OCR en croisant numero de boite et FNUCI.
        </p>
        <div className="flex gap-3 items-center">
          <input
            type="file"
            accept=".xlsx,.xls,.csv"
            onChange={(e) => setDs3File(e.target.files[0])}
            className="text-sm"
          />
          <button
            onClick={async () => {
              if (!ds3File) return;
              setLoading(true);
              setError('');
              setDs3Result(null);
              try {
                const result = await ds3Api.importFile(ds3File);
                setDs3Result(result);
              } catch (err) {
                setError(err.message);
              } finally {
                setLoading(false);
              }
            }}
            disabled={!ds3File || loading}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm disabled:opacity-50"
          >
            {loading ? 'Import...' : 'Importer'}
          </button>
        </div>
        {ds3Result && (
          <div className="mt-4 p-3 bg-green-50 rounded-lg text-sm">
            <p className="font-medium text-green-800">
              {ds3Result.imported} correspondances importées, {ds3Result.updated} mises a jour
            </p>
          </div>
        )}
      </section>
    </div>
  );
}
